/**
 * Logging Utility for BotForge Widget
 * 
 * Provides environment-aware logging that only outputs in development
 * and can be extended to send errors to tracking services in production.
 */

import { ENV } from '../config/environment';

export class Logger {
  private prefix: string;
  
  constructor(prefix: string = 'BotForge') {
    this.prefix = prefix;
  }
  
  /**
   * Format log message with timestamp and prefix
   */
  private formatMessage(message: string): string {
    const timestamp = new Date().toISOString();
    return `[${this.prefix}] ${timestamp} - ${message}`;
  }
  
  /**
   * Send log to parent window for easier debugging in iframe context
   */
  private logToParent(level: string, message: string, data?: any): void {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'widget-log',
          level,
          message: this.formatMessage(message),
          data,
          timestamp: new Date().toISOString()
        }, '*');
      }
    } catch (e) {
      // Ignore cross-origin errors
    }
  }

  /**
   * Log general information (development only)
   */
  log(message: string, data?: any): void {
    if (ENV.enableLogging) {
      if (data !== undefined) {
        console.log(this.formatMessage(message), data);
      } else {
        console.log(this.formatMessage(message));
      }
      this.logToParent('log', message, data);
    }
  }
  
  /**
   * Log informational messages (development only)
   */
  info(message: string, data?: any): void {
    if (ENV.enableLogging) {
      if (data !== undefined) {
        console.info(this.formatMessage(message), data);
      } else {
        console.info(this.formatMessage(message));
      }
      this.logToParent('info', message, data);
    }
  }
  
  /**
   * Log warnings (development and staging)
   */
  warn(message: string, data?: any): void {
    if (ENV.enableLogging || ENV.isStaging) {
      if (data !== undefined) {
        console.warn(this.formatMessage(message), data);
      } else {
        console.warn(this.formatMessage(message));
      }
      this.logToParent('warn', message, data);
    }
  }
  
  /**
   * Log errors (always logged, can be sent to error tracking)
   */
  error(message: string, error?: any): void {
    // Always log errors to console in development
    if (ENV.enableLogging) {
      if (error !== undefined) {
        console.error(this.formatMessage(message), error);
      } else {
        console.error(this.formatMessage(message));
      }
      this.logToParent('error', message, error);
    }
    
    // Send to error tracking service in production/staging
    if (ENV.enableErrorTracking) {
      this.sendToErrorTracking(message, error);
    }
  }
  
  /**
   * Log debug information (development only)
   */
  debug(message: string, data?: any): void {
    if (ENV.enableLogging) {
      if (data !== undefined) {
        console.debug(this.formatMessage(message), data);
      } else {
        console.debug(this.formatMessage(message));
      }
      this.logToParent('debug', message, data);
    }
  }
  
  /**
   * Send error to tracking service (Sentry, etc.)
   * TODO: Implement actual error tracking integration
   */
  private sendToErrorTracking(message: string, error?: any): void {
    // Placeholder for error tracking service integration
    // Example: Sentry.captureException(error, { extra: { message } });
    
    // For now, just prepare the error data
    const errorData = {
      message,
      error: error?.message || error,
      stack: error?.stack,
      environment: ENV.environment,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };
    
    // In production, this would send to your error tracking service
    if (ENV.isDevelopment) {
      console.log('[Error Tracking - Would send:]', errorData);
    }
  }
  
  /**
   * Log a performance metric
   */
  performance(metric: string, duration: number): void {
    if (ENV.enableLogging) {
      console.log(this.formatMessage(`Performance: ${metric} took ${duration}ms`));
    }
  }
  
  /**
   * Create a scoped logger with a different prefix
   */
  scope(scope: string): Logger {
    return new Logger(`${this.prefix}:${scope}`);
  }
}

// Export a default logger instance
export const logger = new Logger('BotForge');

// Export scoped loggers for different parts of the app
export const bootstrapLogger = logger.scope('Bootstrap');
export const firebaseLogger = logger.scope('Firebase');
export const chatLogger = logger.scope('Chat');
export const widgetLogger = logger.scope('Widget');
export const launcherLogger = logger.scope('Launcher');
